import { useCallback, useState } from 'react';
import { Contact } from '../../../types';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { exportContacts, ContactExportFormat } from '../../../services/modules/contactExportService';
import { notify } from '../../../utils/toast';

interface UseContactExportOptions {
    filteredContacts: Contact[];
    getSelectedItems: () => Contact[];
}

export function useContactExport({ filteredContacts, getSelectedItems }: UseContactExportOptions) {
    const { currentOrganization } = useOrganization();
    const [isExporting, setIsExporting] = useState(false);

    const runExport = useCallback(async (contactIds: string[] | undefined, format: ContactExportFormat) => {
        if (!currentOrganization?.id) {
            notify.error('Selecione uma organização para exportar contatos.');
            return;
        }

        const targets = contactIds ?? filteredContacts.map((c) => c.id);
        if (targets.length === 0) {
            notify.info('Nenhum contato para exportar.');
            return;
        }

        setIsExporting(true);
        try {
            await exportContacts({
                organizationId: currentOrganization.id,
                contactIds: contactIds,
                format,
            });
            notify.success(
                contactIds
                    ? `${targets.length} contato(s) exportado(s).`
                    : `${targets.length} contato(s) exportado(s) da lista atual.`
            );
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Erro ao exportar contatos.';
            notify.error(message);
        } finally {
            setIsExporting(false);
        }
    }, [currentOrganization?.id, filteredContacts]);

    const handleExportAll = useCallback(
        (format: ContactExportFormat = 'csv') => runExport(filteredContacts.map((c) => c.id), format),
        [runExport, filteredContacts]
    );

    const handleExportSelected = useCallback(
        (format: ContactExportFormat = 'csv') => {
            const selected = getSelectedItems();
            runExport(selected.map((c) => c.id), format);
        },
        [getSelectedItems, runExport]
    );

    const handleExportOne = useCallback(
        (contact: Contact, format: ContactExportFormat = 'csv') => runExport([contact.id], format),
        [runExport]
    );

    return {
        isExporting,
        handleExportAll,
        handleExportSelected,
        handleExportOne,
    };
}
